import type { ChatMessage, ToolCallStatus } from "../../api/contracts/chat";
import { formatUsd } from "../../format";
import { AssistantMarkdown } from "../assistant-markdown";
import { ToolCallStep, ToolCallGroup } from "./tool-call-step";
import { summarizeInputJson } from "./summarize-input";

function parseJson(s: string | null): unknown {
  if (!s) return undefined;
  try {
    return JSON.parse(s);
  } catch {
    return undefined;
  }
}

/**
 * One persisted Copilot message. User turns read as a subtle right-leaning
 * bubble; assistant turns render as GitHub-flavored markdown (shared
 * AssistantMarkdown) followed by their tool calls, collapsed into one
 * disclosure so the answer reads as a single clean response. Tool / system
 * rows are folded into the assistant treatment for the rare case they carry
 * visible content.
 */

export function ChatMessageView({ message }: { message: ChatMessage }) {
  const isUser = message.role === "user";
  const content = message.content ?? "";

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[88%] rounded-md bg-secondary/40 px-3 py-2 type-body whitespace-pre-wrap text-foreground">
          {content}
        </div>
      </div>
    );
  }

  const hasUsage =
    (message.input_tokens ?? null) !== null ||
    (message.output_tokens ?? null) !== null ||
    (message.cost_usd ?? null) !== null;

  return (
    <div className="flex flex-col gap-1.5">
      {content.trim().length > 0 ? (
        <AssistantMarkdown content={content} />
      ) : null}
      {message.tool_calls.length > 0 ? (
        <ToolCallGroup count={message.tool_calls.length}>
          {message.tool_calls.map((tc) => (
            <ToolCallStep
              key={tc.uid}
              name={tc.tool_name}
              summary={summarizeInputJson(tc.input_json)}
              status={tc.status as ToolCallStatus}
              reason={tc.verdict_reason}
              input={parseJson(tc.input_json)}
              expanded
            />
          ))}
        </ToolCallGroup>
      ) : null}
      {hasUsage ? (
        <p className="type-subtext text-muted-foreground/70">
          {message.input_tokens ?? "–"} in / {message.output_tokens ?? "–"} out
          tokens
          {message.cost_usd != null ? ` · ${formatUsd(message.cost_usd)}` : ""}
        </p>
      ) : null}
    </div>
  );
}
